import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { floorKeep, isNotEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import {
  getYouthMapModelClusterFlowV2,
  getYouthMapModelClusterMallFlowCoefficient,
} from '@/common/api/youth';
import { antPrefix } from '@/common/components/config-v2';
import { format } from 'echarts';
import { Form, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { v4 } from 'uuid';
import cs from 'classnames';
import styles from './index.module.less';
import dayjs from 'dayjs';
import V2BarLineChart from '@/common/components/Charts/V2BarLineChart';
import V2Form from '@/common/components/Form/V2Form';
import V2FormRangePicker from '@/common/components/Form/V2FormRangePicker/V2FormRangePicker';
import V2Title from '@/common/components/Feedback/V2Title';
import IconFont from '@/common/components/IconFont';
import DailyFlowTrend from './DailyFlowTrend';
import FlowTable from './FlowTable';

// 月度客流图表 tooltip 中各系列的单位后缀：客流系列单位为“万(人次)”，占比系列单位为“%”
const monthlyFlowChartUnitMap = { '基准月日均客流': 'W', '对比月日均客流': 'W', '全市客流占比': '%' };
const DEFAULT_MALL_FLOW_COEFFICIENT = 1;
/**
 * 格式化数字为金额显示（万），传入数字计数单位为“元”。
 * @param num 数字，传入数字计数单位为“元”。
 * @param digits 保留小数位数。
 */
const formatNumToTenThousand = (num: any, digits = 2) => {
  if (typeof num !== 'number' || isNaN(num)) {
    return '';
  }
  return floorKeep(num / 10000, 1, 3, digits);
};

/**
 * 商场模块-商场客流（地图详情专用）
 * @description PDF 页面继续使用 MallPassengerFlow/index.tsx，本组件仅承载地图详情的新客流表格。
 * @demo
 */
const Component: FC<{
  /** 商圈id */
  modelClusterId: number,
  /** 模型配置ID */
  modelConfigId: number,
  /** 当前商圈详情 */
  detail: any,
  /** 无数据的时候是否展示空状态 */
  showEmpty?: boolean,
  showModule?: boolean,
  setShowModule?: any,
}> = ({
  modelClusterId,
  modelConfigId,
  detail,
  // showEmpty,
  showModule,
  setShowModule,
}) => {
  const [form] = Form.useForm();
  /* state */
  const [flowData, setFlowData] = useState<any>({});
  const [holidayDateMap, setHolidayDateMap] = useState<Record<string, string>>({});
  const latestMonthRef = useRef<string>('');
  const [flowRange, setFlowRange] = useState<string[]>([]);
  const [outerText, setOuterText] = useState<string>('');
  const [customClass, setCustomClass] = useState<string>('');
  const [monthFilterOpen, setMonthFilterOpen] = useState<boolean>(false);
  const [searchFilterHover, setSearchFilterHover] = useState<boolean>(false);
  const [flowType, setFlowType] = useState<'daily' | 'annual'>('daily');
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const [flowCoefficient, setFlowCoefficient] = useState<number | undefined>(undefined);
  const [flowCoefficientLoading, setFlowCoefficientLoading] = useState<boolean>(true);
  const showMonthlyFlowChart = Array.isArray(flowData.monthlyFlowChartData?.month) && !!flowData.monthlyFlowChartData?.month.length;
  const showRankChart = Array.isArray(flowData.rankData?.month) && !!flowData.rankData?.month.length;

  useEffect(() => {
    setCustomClass(`custom-range-picker-${v4()}`);
  }, []);

  /** 月度客流图表数据，避免重复渲染 */
  // 系列组成：基准月日均客流(柱) + 对比月日均客流(柱)，取值「万人次」共用左轴；全市客流占比(线) 共用右轴(百分比)
  const monthlyFlowChartSeriesData = useMemo(() => [{
    name: '基准月日均客流',
    type: 'bar',
    unit: 'W',
    data: flowData.monthlyFlowChartData?.baseMonthFlow || [],
    yAxisIndex: 0,
  }, {
    name: '对比月日均客流',
    type: 'bar',
    unit: 'W',
    data: flowData.monthlyFlowChartData?.compareMonthFlow || [],
    yAxisIndex: 0,
  }, {
    name: '全市客流占比',
    type: 'line',
    unit: '%',
    data: flowData.monthlyFlowChartData?.cityFlowRate || [],
    yAxisIndex: 1,
  }], [flowData.monthlyFlowChartData]);

  /** 市内商场排名分析图表数据，避免重复渲染 */
  // 系列组成：市内商场客流指数(线,左轴) + 本商场客流(线,左轴,携带同比描述) + 市内排名(线,右轴,数值越小排名越靠前,故右轴反转)
  const rankSeriesData = useMemo(() => [{
    name: '市内商场客流指数',
    type: 'line',
    unit: 'W',
    data: flowData.rankData?.cityShopData || [],
    yAxisIndex: 0,
  }, {
    name: '本商场客流', // 注意下面用于名称匹配处理展示文本
    type: 'line',
    unit: 'W',
    data: flowData.rankData?.shopData || [],
    yAxisIndex: 0,
  }, {
    name: '市内排名',
    type: 'line',
    unit: '',
    data: flowData.rankData?.rank || [],
    yAxisIndex: 1,
  }], [flowData.rankData]);

  /* hooks */
  useEffect(() => {
    if (modelClusterId && modelConfigId) {
      methods.init();
    }
  }, [modelClusterId, modelConfigId]);

  useEffect(() => {
    if (!modelConfigId) return;
    setFlowCoefficientLoading(true);
    getYouthMapModelClusterMallFlowCoefficient({ modelConfigId }).then((res) => {
      setFlowCoefficient(res?.coefficient || DEFAULT_MALL_FLOW_COEFFICIENT);
    }, (error) => {
      console.log(error);
      setFlowCoefficient(DEFAULT_MALL_FLOW_COEFFICIENT);
    }).finally(() => {
      setFlowCoefficientLoading(false);
    });
  }, [modelConfigId]);

  /* methods */
  const methods = useMethods({
    init() {
      methods.fetchFlowData(1, undefined, undefined, true, true);
    },
    fetchFlowData(periodType: number, startMonth?: string, endMonth?: string, needFetchHolidays = false, isInitial = false) {
      getYouthMapModelClusterFlowV2({
        id: modelClusterId,
        modelConfigId,
        periodType,
        startMonth,
        endMonth,
      }).then((res) => {
        const data = res || {};
        const searchMonthDate = data.dailyFlowChart?.monthDate;
        methods.setFlowData(data, isInitial);
        setTableRefreshKey((state) => state + 1);
        if (needFetchHolidays && searchMonthDate) {
          methods.fetchHolidays(searchMonthDate.slice(0, 4));
        }
      });
    },
    setFlowData(data, isInitial = false) {
      if (isInitial) {
        // 首次进入时：以接口返回的 flowCard.month 作为「最新月份」，默认选中当月（当月无法回退的历史月份由 disabledDate 限制）
        const flowCardMonth = data?.flowCard?.month
          ? dayjs(String(data.flowCard.month)).format('YYYY-MM')
          : dayjs().format('YYYY-MM'); // 优先读取data?.flowCard?.month，否则默认当月
        latestMonthRef.current = flowCardMonth;
        form.setFieldValue('range', [flowCardMonth, flowCardMonth]);
        setFlowRange([flowCardMonth, flowCardMonth]);
        setOuterText(dayjs(flowCardMonth).format('YYYY.MM'));
      }
      // 接口返回 flowCard 为空表示无客流数据，控制整个模块是否展示
      setShowModule(data.flowCard !== null);
      // barChart 对应「月日均客流趋势」图：lastYear=基准月日均客流，thisYear=对比月日均客流，flowRate=全市客流占比
      const monthlyFlowChartDataMonth = Array.isArray(data?.barChart?.month) ? data.barChart.month : [];
      setFlowData((previousData) => ({
        ...previousData,
        ...data,
        rankAnalyze: data.rankAnalyze,
        monthlyFlowChartData: {
          month: monthlyFlowChartDataMonth,
          // 基准月日均客流：基准月为该数据轴上月份往前推一年对应的月份
          baseMonthFlow: Array.isArray(data?.barChart?.lastYear) ? data.barChart.lastYear.map((item: number, index: number) => {
            const month = monthlyFlowChartDataMonth[index];
            const monthText = month && dayjs(month).isValid() ? dayjs(new Date(month)).subtract(1, 'year').format('YY年M月') : '';
            return { value: formatNumToTenThousand(item), label: monthText ? `${monthText}日均客流` : '基准月日均客流' };
          }) : [],
          // 对比月日均客流：即数据轴当前月份
          compareMonthFlow: Array.isArray(data?.barChart?.thisYear) ? data.barChart.thisYear.map((item: number, index: number) => {
            const month = monthlyFlowChartDataMonth[index];
            const monthText = month && dayjs(month).isValid() ? dayjs(new Date(month)).format('YY年M月') : '';
            return { value: formatNumToTenThousand(item), label: monthText ? `${monthText}日均客流` : '对比月日均客流' };
          }) : [],
          // 全市客流占比：空值以空字符串占位，保证折线不断裂
          cityFlowRate: Array.isArray(data?.barChart?.flowRate) ? data.barChart.flowRate.map((item) => isNotEmpty(item) ? item : '') : [],
        },
        rankData: {
          month: Array.isArray(data?.lineChart?.month) ? data.lineChart.month : [],
          cityShopData: Array.isArray(data?.lineChart?.top50) ? data.lineChart.top50.map((item: number) => formatNumToTenThousand(item)) : [],
          shopData: Array.isArray(data?.lineChart?.monthAvg) ? data.lineChart.monthAvg.map((item: number, index: number) => ({ value: formatNumToTenThousand(item), desc: data.lineChart?.overRate?.[index] || '' })) : [],
          rank: Array.isArray(data?.lineChart?.rank) ? data.lineChart.rank : [],
        },
        dailyFlowChartData: {
          monthDate: data.dailyFlowChart?.monthDate || '',
          monthlyFlow: data.dailyFlowChart?.monthlyFlow ?? null,
          monthlyAvgFlow: data.dailyFlowChart?.monthlyAvgFlow ?? null,
          dateList: Array.isArray(data.dailyFlowChart?.dateList) ? data.dailyFlowChart.dateList : [],
          dailyFlowList: Array.isArray(data.dailyFlowChart?.dailyFlowList) ? data.dailyFlowChart.dailyFlowList : [],
        },
      }));
    },
    fetchHolidays(year: string) {
      // 拉取指定年份的法定节假日数据，仅取「休息日」(isOffDay=true)，建立 date → 节日名 映射，用于日客流趋势图中标记节假日
      fetch(`https://cdn.jsdelivr.net/gh/NateScarlet/holiday-cn@master/${year}.json`)
        .then((response) => response.json())
        .then((holidayData) => {
          const holidayMap: Record<string, string> = {};
          (holidayData.days || []).forEach((item: { date: string; isOffDay: boolean; name: string }) => {
            if (item.isOffDay) {
              holidayMap[item.date] = item.name;
            }
          });
          setHolidayDateMap(holidayMap);
        })
        .catch((error) => console.log(error));
    },
    openPicker() {
      // 点击外部标题区时展开月份选择弹层：原生日历弹层已隐藏则触发内部 picker 的点击打开
      const popupElement = document.querySelector(`.${customClass}`) as any;
      if (!popupElement || popupElement.classList.contains(`${antPrefix}-picker-dropdown-hidden`)) { // 弹层显示中
        (document.querySelector(`.${customClass}-form .${antPrefix}-picker`) as any).click();
      }
    },
    onOpenChange(newOpen) {
      setMonthFilterOpen(newOpen);
    },
    onChange() {
      // 月份范围选择改变：同步外部标题文案，并按「日均/年均」对应 periodType(1/2) 重新拉取客流数据
      const value = form.getFieldValue('range');
      if (!value?.length) {
        setFlowRange([]);
        setOuterText('');
        return;
      }
      setFlowRange(value);
      setOuterText(value[0] === value[1] ? dayjs(value[0]).format('YYYY.MM') : `${dayjs(value[0]).format('YYYY.MM')}-${dayjs(value[1]).format('YYYY.MM')}`);
      methods.fetchFlowData(flowType === 'daily' ? 1 : 2, value[0], value[1]);
    },
    clear(event) {
      // 点击清除按钮清空月份选择，并触发 onChange 同步状态与重新拉取(此时 range 为空数组)
      event.stopPropagation();
      form.setFieldValue('range', undefined);
      methods.onChange();
    },
    disabledDate(current) {
      // 月份选择限制在「最新月份往前6个月(含当月)」区间内，即最早可选 5 个月前
      const latestMonth = latestMonthRef.current;
      if (!current || !latestMonth) return false;
      const currentMonth = current.format('YYYY-MM');
      const earliestMonth = dayjs(latestMonth).subtract(5, 'month').format('YYYY-MM');
      return currentMonth < earliestMonth || currentMonth > latestMonth;
    },
    getRangeValueFromEvent(...args) {
      const dateString = args[1];
      return Array.isArray(dateString) && dateString.filter((item) => isNotEmpty(item)).length ? dateString : [];
    },
    getRangeValueProps(value) {
      return {
        value: Array.isArray(value) && value.filter((item) => isNotEmpty(item)).length ? [dayjs(value[0], 'YYYY-MM'), dayjs(value[1], 'YYYY-MM')] : undefined,
      };
    },
    changeFlowType(event) {
      // 切换「日均/年均」：periodType 1=日均 2=年均，保持当前月份范围不变，重新拉取客流数据
      const nextFlowType = event.target.value;
      setFlowType(nextFlowType);
      const value = form.getFieldValue('range');
      methods.fetchFlowData(nextFlowType === 'daily' ? 1 : 2, value?.[0], value?.[1]);
    },
    showSearchFilterHover() {
      setSearchFilterHover(true);
    },
    hideSearchFilterHover() {
      setSearchFilterHover(false);
    },
    getRankAxisMax(value) {
      return value.max + 50;
    },
    monthlyTooltipFormatter(params) {
      const axisValueLabel = params[0]?.axisValueLabel || '-';
      const dataText = (Array.isArray(params) ? params : []).map((item) => {
        const seriesName = item.data?.label || item.seriesName;
        const unit = monthlyFlowChartUnitMap[item.seriesName] || '';
        const value = isNotEmpty(item.value) ? `${format.encodeHTML(item.value)}${unit}` : '-';
        return `<div style='display: flex;justify-content: space-between;align-items: center;gap: 20px;'><span>${item.marker} ${format.encodeHTML(seriesName)}</span><span>${value}</span></div>`;
      }).join('');
      return `<div style='margin-bottom: 4px;'>${axisValueLabel}</div>${dataText}`;
    },
    rankTooltipFormatter(params) {
      // 市内排名分析图 tooltip：客流系列显示 单位 W 与同比描述，排名系列无单位
      const axisValueLabel = params[0]?.axisValueLabel || '-';
      const unitMap = { '市内商场客流指数': 'W', '本商场客流': 'W' };
      const dataText = (Array.isArray(params) ? params : []).map((item) => {
        const descText = item.seriesName === '本商场客流' && isNotEmpty(item.data.desc) ? `（${item.data.desc}）` : '';
        const unit = unitMap[item.seriesName] || '';
        const value = isNotEmpty(item.value) ? `${format.encodeHTML(item.value)}${unit}${descText}` : '-';
        return `<div style='display: flex;justify-content: space-between;align-items: center;gap: 20px;'><span>${item.marker} ${format.encodeHTML(item.seriesName)}</span><span>${value}</span></div>`;
      }).join('');
      return `<div style='margin-bottom: 4px;'>${axisValueLabel}</div>${dataText}`;
    },
  });

  return <div className={styles.mallPassengerFlow}>
    <V2Title
      text='商场客流'
      divider
      className={styles.headerTitle}
    />
    {/* 日均/年均 */}
    <FlowTable
      flowCard={flowData.flowCard}
      flowType={flowType}
      latestMonth={flowRange?.[1]}
      flowCoefficient={flowCoefficient}
      flowCoefficientLoading={flowCoefficientLoading}
      detail={detail}
      tableRefreshKey={tableRefreshKey}
      onFlowTypeChange={methods.changeFlowType}
      datePicker={(
        <div className={styles.headerTitleForm}>
          <span
            onClick={methods.openPicker}
            onMouseOver={methods.showSearchFilterHover}
            onMouseLeave={methods.hideSearchFilterHover}
            className={styles.headerTitleBtn}
          >
            {outerText || '请选择'}
            {searchFilterHover && outerText ? (
              <IconFont
                className={cs([styles.icon, styles.iconClose])}
                iconHref='pc-common-icon-ic_close'
                onClick={methods.clear}
              />
            ) : (
              <IconFont
                iconHref='pc-common-icon-ic_xiaomijiantou'
                className={styles.icon}
                style={{ transform: monthFilterOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            )}
          </span>
          <V2Form
            form={form}
            className={cs([styles.headerTitleFormPicker, `${customClass}-form`])}
          >
            <V2FormRangePicker
              name='range'
              config={{
                onOpenChange: methods.onOpenChange,
                onChange: methods.onChange,
                popupClassName: cs(['v2-form-range-picker-for-custom-set', customClass]),
                placement: 'bottomRight',
                picker: 'month',
                disabledDate: methods.disabledDate,
              }}
              formItemConfig={{
                getValueFromEvent: methods.getRangeValueFromEvent,
                getValueProps: methods.getRangeValueProps,
              }}
            />
          </V2Form>
        </div>
      )}
      showModule={showModule}
    />
    {/* xx月日客流趋势 */}
    <DailyFlowTrend
      detail={detail}
      modelConfigId={modelConfigId}
      dailyFlowChartData={flowData.dailyFlowChartData}
      holidayDateMap={holidayDateMap}
      range={flowRange}
    />
    {/* 月日均客流趋势 */}
    {showMonthlyFlowChart && <div className={styles.chartOuter}>
      <V2BarLineChart
        xAxisData={flowData.monthlyFlowChartData?.month || []}
        seriesData={monthlyFlowChartSeriesData}
        yAxisData={[{ axisLabel: { formatter: '{value}W' } }, { axisLabel: { formatter: '{value}%' }, splitLine: { show: false } }]}
        wrapperClassName={styles.monthlyChartWrapper}
        config={{
          legend: { width: 'auto' },
          tooltip: { formatter: methods.monthlyTooltipFormatter },
        }}
      />
      <div className={styles.chartOuterTitle}>
        <div className={styles.chartOuterTitleInner}>
          <div>月日均客流趋势</div>
          <Tooltip
            title={<div><div>【基准月日均客流】</div><div>作为对比基础的月份，是数据分析商场客流“变化量 / 变化率” 的参照标准。</div><br /><div>【对比月日均客流】</div><div>对比月是指与基准月进行 “直接对比” 的月份，通过与基准月数据的差值 / 比值，体现近6月商场客流的变动趋势情况。</div><br /><div>【全市客流占比】</div><div>本商场在全市总客流的占比值。</div></div>}
            placement='top'
            color='#fff'
            overlayInnerStyle={{ width: '314px', boxSizing: 'border-box', borderRadius: '4px', padding: '16px', color: 'rgba(0, 0, 0, 0.85)' }}
          >
            <QuestionCircleOutlined className='fs-12 c-666 ml-2'/>
          </Tooltip>
        </div>
      </div>
    </div>}

    <div className={styles.divider}></div>
    {/* 市内商场排名分析 */}
    {isNotEmpty(flowData.rankAnalyze) && <div className={styles.rankAnalyze}>{flowData.rankAnalyze}</div>}
    {showRankChart && <div className={styles.chartOuter}>
      <V2BarLineChart
        xAxisData={flowData.rankData?.month || []}
        seriesData={rankSeriesData}
        yAxisData={[{ axisLabel: { formatter: '{value}W' } }, { inverse: true, max: methods.getRankAxisMax, splitLine: { show: false } }]}
        wrapperClassName={styles.rankChartWrapper}
        config={{ tooltip: { formatter: methods.rankTooltipFormatter } }}
      />
      <div className={styles.chartOuterTitle}>
        <div className={styles.chartOuterTitleInner}>
          <div>市内商场排名分析</div>
          <Tooltip
            title={<div><div>【市内商场客流指数】</div><div>反映市内典型商场的客流趋势变动的综合量化指标。</div><br /><div>【计算逻辑】</div><div>通过统计方法（标准差）找到这个“正常区间”，并计算出该区间内商场的「平均客流水平」。</div><br /><div>【具体方法】</div><div>1.计算全市商场客流值的标准差</div><div>2.客流值低于标准差的商场剔除计算（会拉低指数）</div><div>3.客流值高于2倍标准层的商场剔除计算（会拉高指数）</div><div>4.剩下留下来的视为典型商场，计算客流平均值</div></div>}
            placement='top'
            color='#fff'
            overlayInnerStyle={{ width: '314px', boxSizing: 'border-box', borderRadius: '4px', padding: '16px', color: 'rgba(0, 0, 0, 0.85)' }}
          >
            <QuestionCircleOutlined className='fs-12 c-666 ml-2'/>
          </Tooltip>
        </div>
      </div>
    </div>}
  </div>;
};

export default Component;
